import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import spaceImage from '../images/space.webp';
import puzzlePlanetLogo from '../images/logos/title.webp';
import { authApi } from '../services/api';
import { validateUsername } from '../utils/validation';
import Spinner from './Spinner';

const TrialSignupUserPassword: React.FC = () => {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const [username, setUsername] = useState('');
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [code, setCode] = useState<string | null>(null);

    useEffect(() => {
        const signupCode = searchParams.get('code');
        if (signupCode) {
            setCode(signupCode);
        } else {
            setError('Invalid or missing signup code. Please check your email link.');
        }
    }, [searchParams]);

    const validateForm = (): string | null => {
        // Validate username using shared validation function
        const usernameValidation = validateUsername(username);
        if (!usernameValidation.isValid) {
            return usernameValidation.error || 'Invalid username';
        }
        if (!password) {
            return 'Password is required';
        }
        if (password.length < 8) {
            return 'Password must be at least 8 characters long';
        }
        if (password !== confirmPassword) {
            return 'Passwords do not match';
        }
        return null;
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError(null);

        const validationError = validateForm();
        if (validationError) {
            setError(validationError);
            return;
        }

        if (!code) {
            setError('Invalid or missing signup code. Please check your email link.');
            return;
        }

        setLoading(true);

        try {
            const response = await authApi.trialSignupSetPassword({
                code,
                username: username.trim(),
                password,
                confirmationPassword: confirmPassword,
            });

            if (response.status === 'success') {
                // Navigate to the payment page (Step 3/3)
                navigate(
                    response.activatedTrial ? '/trial-signup-success' : '/trial-signup-payment'
                );
            } else {
                setError(response.message || 'Failed to complete signup');
            }
        } catch (err: any) {
            console.error('Complete email signup error:', err);
            setError('An unexpected error occurred. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div
            className='min-h-screen flex flex-col items-center justify-center py-12 px-4 overflow-x-hidden w-full'
            style={{
                backgroundImage: `url(${spaceImage})`,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                backgroundAttachment: 'fixed',
                backgroundRepeat: 'no-repeat',
            }}
        >
            <div className='flex flex-col items-center space-y-8 max-w-2xl w-full'>
                {/* Title Logo */}
                <div className='flex justify-center mb-8'>
                    <img
                        src={puzzlePlanetLogo}
                        alt='Puzzle Planet'
                        className='h-24 md:h-32 w-auto'
                    />
                </div>

                {/* Step Indicator */}
                <div className='text-center mb-4'>
                    <p className='text-xl md:text-2xl font-semibold' style={{ color: '#FFB502' }}>
                        {import.meta.env.VITE_NO_CREDIT_CARD_REQUIRED === 'true'
                            ? 'Step 2'
                            : 'Step 2/3'}
                    </p>
                </div>

                {/* Heading */}
                <h1
                    className='text-4xl md:text-5xl font-bold text-center'
                    style={{ color: '#FFB502' }}
                >
                    Create your account
                </h1>

                {/* Form */}
                <form onSubmit={handleSubmit} className='w-full max-w-md space-y-6'>
                    {/* Username Field */}
                    <div>
                        <label
                            htmlFor='username'
                            className='block text-sm font-medium mb-2'
                            style={{ color: '#FFFFFF' }}
                        >
                            Username
                        </label>
                        <input
                            type='text'
                            id='username'
                            value={username}
                            onChange={e => setUsername(e.target.value)}
                            required
                            disabled={loading || !code}
                            autoComplete='off'
                            className='w-full px-6 py-4 text-lg border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-colors duration-200 bg-white text-gray-900 placeholder-gray-500 disabled:opacity-50 disabled:cursor-not-allowed'
                            placeholder='Choose a username'
                            minLength={3}
                        />
                        <p className='mt-2 text-sm' style={{ color: '#CCCCCC' }}>
                            Characters allowed: letters, numbers, _, -
                        </p>
                    </div>

                    {/* Password Field */}
                    <div>
                        <label
                            htmlFor='password'
                            className='block text-sm font-medium mb-2'
                            style={{ color: '#FFFFFF' }}
                        >
                            Password
                        </label>
                        <input
                            type='password'
                            id='password'
                            value={password}
                            onChange={e => setPassword(e.target.value)}
                            required
                            disabled={loading || !code}
                            autoComplete='new-password'
                            className='w-full px-6 py-4 text-lg border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-colors duration-200 bg-white text-gray-900 placeholder-gray-500 disabled:opacity-50 disabled:cursor-not-allowed'
                            placeholder='Create a password'
                            minLength={8}
                        />
                    </div>

                    {/* Confirm Password Field */}
                    <div>
                        <label
                            htmlFor='confirmPassword'
                            className='block text-sm font-medium mb-2'
                            style={{ color: '#FFFFFF' }}
                        >
                            Confirm Password
                        </label>
                        <input
                            type='password'
                            id='confirmPassword'
                            value={confirmPassword}
                            onChange={e => setConfirmPassword(e.target.value)}
                            required
                            disabled={loading || !code}
                            autoComplete='new-password'
                            className='w-full px-6 py-4 text-lg border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-colors duration-200 bg-white text-gray-900 placeholder-gray-500 disabled:opacity-50 disabled:cursor-not-allowed'
                            placeholder='Confirm your password'
                            minLength={8}
                        />
                    </div>

                    {/* Help Text */}
                    <p className='mt-4 text-center text-white text-sm'>
                        If you have any issues during signup, please email{' '}
                        <a
                            href='mailto:support@mindfulgames.io'
                            className='text-orange-400 hover:text-orange-300 underline transition-colors duration-200'
                        >
                            support@mindfulgames.io
                        </a>
                    </p>

                    {/* Error Message */}
                    {error && (
                        <div className='mt-4 text-center text-red-400 text-sm max-w-md'>
                            {error}
                        </div>
                    )}

                    {/* Submit Button */}
                    <button
                        type='submit'
                        disabled={loading || !code}
                        className='w-full px-8 py-4 text-lg font-semibold bg-gradient-to-b from-yellow-400 to-orange-500 text-black rounded-lg hover:from-yellow-500 hover:to-orange-600 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:ring-offset-2 transform transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed'
                    >
                        {loading ? 'Processing...' : 'Next'}
                    </button>
                </form>

                {/* Loading Overlay */}
                {loading && (
                    <div className='fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50'>
                        <div className='bg-white rounded-2xl shadow-2xl p-8 max-w-md mx-4 text-center'>
                            <div className='mb-6'>
                                <div className='w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4'>
                                    <Spinner size='lg' color='blue' />
                                </div>
                            </div>
                            <h2 className='text-2xl font-bold text-gray-900 mb-2'>
                                Creating Your Account
                            </h2>
                            <p className='text-gray-600 mb-4'>
                                Please do not navigate away from the page
                            </p>
                            <div className='text-sm text-gray-500'>
                                This may take a few moments...
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default TrialSignupUserPassword;
